import {ActionReference, GameObjectReference, GameState} from "@shared/types";
import {LitElement, TemplateResult, css, html, nothing} from "lit";
import {customElement, property} from "lit/decorators.js";
import {getSaves, getState, performAction, saveGame} from "../services/routeService";
import {getCookie} from "../services/cookieService";
import {Save, SavePopup} from "./SavePopup";
import {getPlayerSessionId} from "../helpers";

@customElement("game-canvas")
export class GameCanvas extends LitElement {
    public static styles = css`
        .game {
            height: 100%;
            width: 75%;
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: auto auto 1fr auto;
            grid-column-gap: 0px;
            grid-row-gap: 0px;
            float: left;
        }

        .inventory {
            float: right;
            width: 25%;
            animation: slidein 1.5s forwards, fadein 3s;
            transform: translateY(-100%);
        }

        @keyframes slidein {
            from {
                transform: translateY(-100%);
            }
            to {
                transform: translateY(0);
            }
        }

        @keyframes fadein {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }

        .inventory h2 {
            padding: 5px;
            margin-bottom: 0;
        }

        .inventory ul {
            margin: 0;
        }

        .title {
            text-align: center;
            margin-top: 10px;
        }

        .header {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex-grow: 1;
            position: relative;
            margin-top: 10px;
        }

        .header img {
            width: 100%;
            height: auto;
            image-rendering: pixelated;
        }

        .header img:nth-child(n + 2) {
            position: absolute;
        }

        .content {
            flex-grow: 1;
            overflow: auto;
            margin-top: 10px;
            padding: 0 10px;
        }

        .content p {
            margin: 0 0 16px 0;
        }

        .content p:last-of-type {
            margin: 0;
        }

        .footer {
            border-radius: 10px 10px 0 0;
            background-color: black;
            margin-top: 10px;
            display: flex;
            height: 150px;
        }

        .footer .buttons {
            display: flex;
            flex-direction: column;
            overflow: auto;
            padding: 10px 10px 0 10px;
        }

        .footer .button {
            color: #00FF00;
            padding: 5px 10px;
            margin: 0 0 10px 10px;
            text-transform: uppercase;
            cursor: pointer;
            display: inline-block;
            user-select: none;
        }

        .footer .button.active,
        .footer .button:hover {
            color: #00c000;
        }

        save-popup {
            left: 40%;
            top: 10%;
            transform: none;
        }
    `;


    private roomTitle?: string;
    private roomImages?: string[];
    private contentText?: string[];
    private inventory?: string[];
    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];

    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();


    @property() private saves: Save[] = [];
    @property({type: Boolean}) public isLoggedIn: boolean = false;

    public constructor() {
        super();

        const cookie: string | null = getCookie("access_token");
        if (cookie) {
            this.isLoggedIn = true;
            void this.getSaves();
        }
    }

    public connectedCallback(): void {
        super.connectedCallback();

        void this.refreshState();
    }

    private async refreshState(): Promise<void> {
        const state: GameState = await getState();

        this.updateState(state);
    }

    private updateState(state: GameState): void {
        //Reset the component
        this.roomTitle = state.roomTitle;
        this.roomImages = state.roomImages;
        this.contentText = state.text;
        this.actionButtons = state.actions;
        this.gameObjectButtons = state.objects;
        this.inventory = state.inventory;

        this.selectedActionButton = undefined;
        this.selectedGameObjectButtons.clear();

        this.requestUpdate();
    }

    private async handleClickAction(button: ActionReference): Promise<void> {
        if (button.needsObject) {
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();

            this.requestUpdate();
        } else {
            const state: any = await performAction(button.alias);

            if (state === undefined) {
                return;
            }

            this.updateState(state);
        }
    }

    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (!this.selectedActionButton) {
            return;
        }

        this.selectedGameObjectButtons.add(button);

        const state: GameState | undefined = await performAction(
            this.selectedActionButton.alias,
            [...this.selectedGameObjectButtons].map((e) => e.alias)
        );

        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }

    protected render(): TemplateResult {
        return html`
            <div class="game">
                ${this.renderTitle()} ${this.renderHeader()} ${this.renderContent()} ${this.renderFooter()}
            </div>
            ${this.renderInventory()}
        `;
    }

    private renderTitle(): TemplateResult {
        if (this.roomTitle) {
            return html`
                <div class="title">${this.roomTitle}</div>`;
        }

        return html`${nothing}`;
    }

    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 0) {
            return html`
                <div class="header">
                    ${this.roomImages?.map((url) => html`<img src="/assets/img/rooms/${url}.png"/>`)}
                </div>
            `;
        }

        return html`${nothing}`;
    }

    private renderInventory(): TemplateResult {
        if (this.inventory && this.inventory.length > 0) {
            return html`
                <div class="inventory">
                    <h2>Inventory</h2>
                    <ul>
                        ${this.inventory.map(item => html`
                            <li>${item}</li>`)}
                    </ul>
                </div>
            `;
        }

        return html`${nothing}`;
    }


    // eslint-disable-next-line @typescript-eslint/require-await
    private async handleSaveClick(): Promise<void> {
        const popup: SavePopup | null | undefined = this.shadowRoot?.querySelector("save-popup");
        if (popup) {
            popup.style.display = "block";
        }
    }

    private async getSaves(): Promise<void> {
        const access_token: string | null = getCookie("access_token");

        if (!access_token) {
            return;
        }

        this.saves = await getSaves(access_token);
        this.saves.sort((a, b) => new Date(b.updatedAt!).getTime() - new Date(a.updatedAt!).getTime());
        this.saves.splice(0, 0, {name: "New save", save_id: "0"});
    }

    private async handleSaveSelected(e: CustomEvent): Promise<void> {
        const selectedSave: any = e.detail;
        const token: string = getCookie("access_token")!;
        const popup: SavePopup | null | undefined = this.shadowRoot?.querySelector("save-popup");

        if (!popup) {
            return;
        }

        if (selectedSave.name === "New save" || !token) {
            await saveGame(getPlayerSessionId(), token);
        } else {
            await saveGame(getPlayerSessionId(), token, selectedSave.save_id);
        }

        await this.getSaves();
        this.requestUpdate();
    }

    private handleMainMenuClick(): void {
        const confirmLeave: boolean = window.confirm("Are you sure you want to leave? Any unsaved game actions will be lost.");
        if (confirmLeave) {
            document.location.pathname = "index.html";
        }
    }


    private renderContent(): TemplateResult {
        return html`
            <div class="content">${this.contentText?.map((text) => html`<p>${text}</p>`)}</div>

            <save-popup .saves=${this.saves} @save-selected=${this.handleSaveSelected}></save-popup>
        `;
    }

    private renderFooter(): TemplateResult {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this.actionButtons?.map(
                            (button) => html`<a
                                class="button ${this.selectedActionButton === button ? "active" : ""}"
                                @click=${(): void => void this.handleClickAction(button)}
                            >${button.label}</a
                            >`
                        )}
                        ${this.isLoggedIn ? html`<a class="button"
                                                    @click=${this.handleSaveClick}>Save</a>` : nothing}
                        <a class="button"
                           @click=${this.handleMainMenuClick}>Main Menu</a>
                    </div>
                    <div>
                        ${this.selectedActionButton
                            ? this.gameObjectButtons?.map(
                                (button) => html`<a
                                    class="button ${this.selectedGameObjectButtons.has(button)
                                        ? "active"
                                        : ""}"
                                    @click=${(): void => void this.handleClickObject(button)}
                                >${button.name}</a
                                >`
                            )
                            : nothing}
                    </div>
                </div>
            </div>
        `;
    }
}
